import React, { useMemo, useEffect, useState } from "react";
import MultiLingualContent from "../config/multilingualContent";


const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value && typeof value === "object") {
    const candidate = value.y ?? value.value ?? value.v;
    return Number.isFinite(candidate) ? candidate : 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const hexToRgb = (hex) => {
  if (!hex || typeof hex !== "string") return null;
  const clean = hex.replace("#", "").trim();
  if (![3, 6].includes(clean.length)) return null;

  const full = clean.length === 3
    ? clean.split("").map((c) => c + c).join("")
    : clean;

  const int = Number.parseInt(full, 16);
  if (Number.isNaN(int)) return null;

  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255
  };
};

const getTextColor = (bgColor) => {
  const rgb = hexToRgb(bgColor);
  if (!rgb) return "#111";
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.6 ? "#111" : "#fff";
};

const BarRaceChart = ({ sourceOptions }) => {
  const categories = sourceOptions?.xAxis?.categories || [];
  const series = sourceOptions?.series || [];
  const [frameIndex, setFrameIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    setFrameIndex(0);
    setIsPlaying(false);
  }, [sourceOptions]);

  useEffect(() => {
    if (!isPlaying || categories.length <= 1) return undefined;

    const timer = setInterval(() => {
      setFrameIndex((prev) => {
        if (prev >= categories.length - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 900);

    return () => clearInterval(timer);
  }, [isPlaying, categories.length]);

  const normalizedSeriesData = useMemo(() => {
    const rawSeries = series.map((s) => ({
      name: s.name,
      color: s.color,
      data: (s.data || []).map((point) => toNumber(point))
    }));

    const totalsByIndex = categories.map((_, idx) =>
      rawSeries.reduce((sum, s) => sum + (s.data[idx] || 0), 0)
    );

    const percentSeries = rawSeries.map((s) => ({
      ...s,
      data: s.data.map((v, idx) => {
        const total = totalsByIndex[idx] || 0;
        return total > 0 ? (v / total) * 100 : 0;
      })
    }));

    return { rawSeries, percentSeries };
  }, [series, categories]);

  const productCards = useMemo(() => {
    return normalizedSeriesData.percentSeries
      .map((item) => ({
        name: item.name,
        color: item.color || "#2f7ed8",
        currentPct: item.data?.[frameIndex] || 0
      }))
      .sort((a, b) => b.currentPct - a.currentPct);
  }, [normalizedSeriesData, frameIndex]);

  if (!categories.length || !series.length) {
    return <p><MultiLingualContent contentID="nodata" /></p>;
  }

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => setIsPlaying((prev) => !prev)}
          disabled={categories.length <= 1}
        >
          {isPlaying ? <MultiLingualContent contentID="pause" /> : <MultiLingualContent contentID="play" />}
        </button>
        <input
          type="range"
          min={0}
          max={Math.max(0, categories.length - 1)}
          value={frameIndex}
          onChange={(e) => {
            setIsPlaying(false);
            setFrameIndex(Number(e.target.value));
          }}
          style={{ width: "240px" }}
        />
        <strong>{categories[frameIndex]}</strong>
      </div>

      <div className="mt-3" style={{ display: "grid", gap: "8px" }}>
        {productCards.map((card) => (
          <div key={card.name} style={{ width: "100%", background: "#eef1f4", borderRadius: "8px", overflow: "hidden" }}>
            <div
              title={`${card.name}: ${card.currentPct.toFixed(2)}%`}
              style={{
                width: `${Math.max(0, Math.min(100, card.currentPct))}%`,
                minWidth: "140px",
                background: card.color,
                color: getTextColor(card.color),
                borderRadius: "8px",
                padding: "4px 8px",
                fontSize: "11px",
                fontWeight: 600,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                whiteSpace: "nowrap"
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", paddingRight: "8px" }}>{card.name}</span>
              <span>{card.currentPct.toFixed(2)}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BarRaceChart;
